import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BlogPost } from '../../types/Blog';
import { Gallery } from '../../types/Gallery';
import { normalizeBlogPost, saveBlogPostsToServer } from '../../utils/BlogAPI';
import { loadLiveGalleries } from '../../utils/GalleryAPI';
import { API_BASE_URL } from '../../utils/apiBase';
import { DEFAULT_LOGO } from '../../config/defaults';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { saveGlobalDisplaySettings } from '../../utils/DisplaySettingsAPI';
import { cloudinaryDeliveryUrl } from '../../utils/imageSeo';
import {
  HOMEPAGE_STORY_LIMIT,
  clampHomepageStoryCount,
  formatStoryDate,
  homepageStories,
  imageSource,
} from '../../utils/blogPresentation';
import sitePaths from '../../utils/sitePaths';
import { normalizeEditorHtml, normalizeEditorHref } from '../../utils/blogHtml';
import { bindPhotoMasonry, flattenPhotoMasonry, layoutPhotoMasonry } from '../../utils/photoMasonry';
import { articleBlockHtml, EditableBlock, EditablePhoto, readArticleBlock } from './articleBlocks';
import BlogPhotoEditor from './BlogPhotoEditor';
import BlogPhotoPicker, { asMasonryColumns, MasonryColumns, PickedPhoto } from './BlogPhotoPicker';
import './BlogManagement.css';

const EMPTY: BlogPost = {
  id: '',
  title: '',
  slug: '',
  excerpt: '',
  content: '<p></p>',
  author: 'Carmen Lorraine',
  publishedAt: '',
  createdAt: '',
  updatedAt: '',
  published: false,
  featured: false,
  categories: [],
  tags: [],
  readingTime: 1,
  status: 'draft',
  scheduledFor: '',
  seoTitle: '',
  metaDescription: '',
  h1: '',
  canonical: '',
  noindex: false,
  category: '',
  faq: [],
  galleryCategory: '',
  galleryIds: [],
  showGalleryBlock: true,
  galleryCount: 6,
  featuredImageAlt: '',
  showToc: true,
};

const POST_CATEGORIES = [
  { slug: 'family', name: 'Family' },
  { slug: 'business-headshots', name: 'Business & Headshots' },
  { slug: 'lifestyle', name: 'Lifestyle' },
  { slug: 'natural', name: 'Natural' },
];

type GalleryFilter = { slug: string; name: string };
const galleryFilters = (sitePaths.NAV_FILTERS as Array<GalleryFilter | undefined>).filter((filter): filter is GalleryFilter => Boolean(filter));

const LINK_PAGES = [
  { label: 'All galleries', href: '/galleries' },
  ...galleryFilters.map((filter) => ({ label: filter.name, href: `/galleries/${filter.slug}` })),
  { label: 'Family photographer', href: '/galleries/family' },
  { label: 'Business photographer', href: '/galleries/business-and-commercial' },
  { label: 'Request a quote', href: '/quote' },
  { label: 'Contact', href: '/contact' },
];

function slugFrom(title: string): string {
  return sitePaths.slugify(title);
}

function chosenCategorySlugs(post: BlogPost, known: Array<{ slug: string }>): string[] {
  const allowed = new Set(known.map((item) => item.slug));
  const ordered = [post.category, ...(post.categories || [])].map((slug) => String(slug || '').trim()).filter(Boolean);
  const unique: string[] = [];
  ordered.forEach((slug) => {
    if (!allowed.has(slug) || unique.includes(slug)) return;
    unique.push(slug);
  });
  return unique;
}

function wordCount(html: string): number {
  return plainText(html).split(/\s+/).filter(Boolean).length;
}

function lockInserts(root: ParentNode) {
  root.querySelectorAll('.blog-rule, .blog-logo, .photo-masonry, .photo-masonry-col, .photo-row, figure').forEach((node) => {
    node.setAttribute('contenteditable', 'false');
    if (node.classList.contains('blog-rule')) node.setAttribute('role', 'separator');
  });
}

function studioLogo() {
  const fallback = {
    url: DEFAULT_LOGO.imageUrl,
    alt: DEFAULT_LOGO.altText || 'Carmen Lorraine Photography',
    width: DEFAULT_LOGO.width || 128,
    height: DEFAULT_LOGO.height || 125,
  };
  try {
    const parsed = JSON.parse(localStorage.getItem('website_logo') || '');
    const url = String(parsed?.imageUrl || '').trim();
    if (!/^https:\/\//i.test(url)) return fallback;
    return {
      url,
      alt: String(parsed.altText || fallback.alt).trim() || fallback.alt,
      width: Number(parsed.width) || fallback.width,
      height: Number(parsed.height) || fallback.height,
    };
  } catch {
    return fallback;
  }
}

function plainText(html: string): string {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function suggestMeta(html: string): string {
  const text = plainText(html);
  if (text.length <= 155) return text;
  const cut = text.slice(0, 155);
  const space = cut.lastIndexOf(' ');
  return (space > 80 ? cut.slice(0, space) : cut).trim();
}

function toLocalInput(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function Hint({ text }: { text: string }) {
  return (
    <span className="blog-hint">
      <span className="blog-hint-mark" title={text} aria-hidden="true">i</span>
      <span className="blog-hint-text">{text}</span>
    </span>
  );
}

function ArticleHelp() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="blog-article-intro" ref={rootRef}>
      <div className="blog-article-head">
        <h2>Article</h2>
        <div className="blog-help">
          <button
            type="button"
            className="blog-help-button"
            aria-label="Article help"
            aria-expanded={open}
            aria-controls="blog-article-help"
            onClick={() => setOpen((value) => !value)}
          >
            <span className="blog-hint-mark" aria-hidden="true">i</span>
            Help
          </button>
        </div>
      </div>
      {open ? (
        <div id="blog-article-help" className="blog-help-popup" role="dialog" aria-label="How to write the article">
          <p>Write the post here. Use Heading for main sections and Subheading inside them. Aim for 1,000–1,400 words.</p>
          <p>Click in the article first. Open Insert for photos, a link, or a logo at that spot. They are optional. The large photo at the top of the post is the Featured image in Photos.</p>
          <ul className="blog-insert-steps">
            <li><strong>Link.</strong> Highlight a few words, then choose a page or a published story. You can also type an address that starts with /, such as /blog/… or /galleries/…. If nothing is highlighted, the page name is written in.</li>
            <li><strong>Photos.</strong> Upload one or more photos, or choose them from a gallery. Choose 1 to 5 columns. One column is a single photograph at full width. More columns make each photograph smaller. Each photo needs a short description.</li>
            <li><strong>Edit.</strong> Click a photograph already in the article. You can change its description and caption, change the columns, reorder the group, or remove a photograph.</li>
            <li><strong>Divider.</strong> Places a fine line between two parts of the article. Click where the break should go, then press Divider.</li>
            <li><strong>Logo.</strong> Places the studio logo where you clicked. Choose small, medium, or large.</li>
          </ul>
          <button type="button" className="blog-text-button" onClick={() => setOpen(false)}>Close</button>
        </div>
      ) : null}
    </div>
  );
}

function storyStatus(post: BlogPost): string {
  const status = post.status || (post.published ? 'published' : 'draft');
  if (status === 'published') return 'Published';
  if (status === 'scheduled') return 'Scheduled';
  return 'Draft';
}

const BlogManagement: React.FC = () => {
  const displaySettings = useDisplaySettings();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOrder, setDragOrder] = useState<BlogPost[] | null>(null);
  const dragSession = useRef<{ pointerId: number; sourceId: string; origin: BlogPost[] } | null>(null);
  const latestOrder = useRef<BlogPost[] | null>(null);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [message, setMessage] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [h1Touched, setH1Touched] = useState(false);
  const [galleryQuery, setGalleryQuery] = useState('');
  const [masonryColumns, setMasonryColumns] = useState<MasonryColumns>(3);
  const [pickerMasonry, setPickerMasonry] = useState(false);
  const [pickerCount, setPickerCount] = useState<1 | null>(null);
  const [photoDraft, setPhotoDraft] = useState<EditableBlock | null>(null);
  const [masonryTick, setMasonryTick] = useState(0);
  const photoBlockRef = useRef<HTMLElement | null>(null);
  const [pickerTarget, setPickerTarget] = useState<'article' | 'featured' | 'add'>('article');
  const [articleWords, setArticleWords] = useState(0);
  const [chooseSpecific, setChooseSpecific] = useState(false);
  const [metaTouched, setMetaTouched] = useState(false);
  const [autoMeta, setAutoMeta] = useState('');
  const [customLink, setCustomLink] = useState('');
  const [insertOpen, setInsertOpen] = useState(false);
  const savedRange = useRef<Range | null>(null);
  const editorHtml = useRef('<p></p>');
  const editorFormRef = useRef<HTMLDivElement | null>(null);
  const sectionControlsRef = useRef<HTMLDivElement | null>(null);

  const [postCategories, setPostCategories] = useState(POST_CATEGORIES);
  const [previewHref, setPreviewHref] = useState('');

  useEffect(() => {
    loadLiveGalleries('cards').then(setGalleries).catch(() => setGalleries([]));
    import('../../utils/BlogAPI').then(({ getBlogPostsFromServer }) => getBlogPostsFromServer().then((loaded) => {
      setPosts((loaded || []).map((post) => normalizeBlogPost(post)));
    })).catch(() => setPosts([]));
    const loadCategories = () => {
      import('../../utils/BlogAPI').then(({ getBlogCategoriesFromServer }) => getBlogCategoriesFromServer().then((loaded) => {
        const next = (loaded || [])
          .map((item) => ({ slug: item.slug, name: item.name || item.slug }))
          .filter((item) => item.slug);
        setPostCategories(next.length ? next : POST_CATEGORIES);
      })).catch(() => setPostCategories(POST_CATEGORIES));
    };
    loadCategories();
    window.addEventListener('blog_categories_updated', loadCategories);
    return () => window.removeEventListener('blog_categories_updated', loadCategories);
  }, []);

  useEffect(() => {
    setArticleWords(wordCount(editing?.content || ''));
  }, [editing?.id, editing?.content]);

  useEffect(() => {
    setInsertOpen(false);
  }, [editing?.id]);

  useLayoutEffect(() => {
    if (!editing) return undefined;
    const form = editorFormRef.current;
    if (!form) return undefined;
    const header = document.querySelector('.admin-actions-bar') as HTMLElement | null;
    const syncStickyVars = () => {
      const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 56;
      const actionsHeight = sectionControlsRef.current
        ? Math.ceil(sectionControlsRef.current.getBoundingClientRect().height)
        : 52;
      form.style.setProperty('--blog-sticky-top', `${headerHeight}px`);
      form.style.setProperty('--blog-actions-h', `${actionsHeight}px`);
    };
    syncStickyVars();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncStickyVars) : null;
    if (header && observer) observer.observe(header);
    if (sectionControlsRef.current && observer) observer.observe(sectionControlsRef.current);
    window.addEventListener('resize', syncStickyVars);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', syncStickyVars);
    };
  }, [editing]);

  useLayoutEffect(() => {
    if (!editing) return undefined;
    const editor = document.getElementById('blog-editor');
    if (!editor) return undefined;
    return bindPhotoMasonry(editor, { simulatePreview: true });
  }, [editing, masonryTick]);

  useEffect(() => {
    const slug = editing?.slug || '';
    if (!slug) {
      setPreviewHref('');
      return undefined;
    }
    let active = true;
    const plain = `/blog/preview/${slug}`;
    fetch(`${API_BASE_URL}/blog-preview-token?slug=${encodeURIComponent(slug)}`, { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (active) setPreviewHref(body && body.path ? body.path : plain);
      })
      .catch(() => {
        if (active) setPreviewHref(plain);
      });
    return () => {
      active = false;
    };
  }, [editing?.slug]);

  const saveAll = async (next: BlogPost[]) => {
    const ordered = next.map((post, index) => ({ ...post, order: index + 1 }));
    const saved = await saveBlogPostsToServer(ordered);
    if (!saved) throw new Error('Save failed');
    setPosts(ordered);
    window.dispatchEvent(new Event('blog_posts_updated'));
  };

  const listed = dragOrder ?? posts;
  const homeCount = clampHomepageStoryCount(displaySettings.homepageBlogCount);
  const home = homepageStories(listed, homeCount);
  const homeRole = new Map<string, 'lead' | 'supporting'>();
  if (home.lead) homeRole.set(home.lead.id, 'lead');
  home.supporting.forEach((post) => homeRole.set(post.id, 'supporting'));

  const movePost = (sourceId: string, targetId: string, list: BlogPost[]): BlogPost[] | null => {
    if (sourceId === targetId) return null;
    const updated = [...list];
    const from = updated.findIndex((post) => post.id === sourceId);
    const to = updated.findIndex((post) => post.id === targetId);
    if (from < 0 || to < 0 || from === to) return null;
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    return updated;
  };

  const scrollWhileDragging = (clientY: number) => {
    const edge = 72;
    const scroller = document.querySelector('.admin-main') as HTMLElement | null;
    const canScroll = scroller && scroller.scrollHeight > scroller.clientHeight + 4;
    if (clientY < edge) {
      if (canScroll && scroller) scroller.scrollTop -= 16;
      else window.scrollBy(0, -16);
    } else if (clientY > window.innerHeight - edge) {
      if (canScroll && scroller) scroller.scrollTop += 16;
      else window.scrollBy(0, 16);
    }
  };

  const startReorder = (event: React.PointerEvent<HTMLButtonElement>, postId: string) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragSession.current = { pointerId: event.pointerId, sourceId: postId, origin: posts };
    latestOrder.current = null;
    setDraggingId(postId);
  };

  const moveReorder = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = dragSession.current;
    if (!session || session.pointerId !== event.pointerId) return;
    event.preventDefault();
    scrollWhileDragging(event.clientY);
    const hit = document.elementFromPoint(event.clientX, event.clientY);
    const card = hit?.closest('[data-post-id]') as HTMLElement | null;
    const targetId = card?.dataset.postId;
    if (!targetId || targetId === session.sourceId) return;
    const next = movePost(session.sourceId, targetId, latestOrder.current ?? session.origin);
    if (!next) return;
    latestOrder.current = next;
    setDragOrder(next);
  };

  const finishReorder = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = dragSession.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragSession.current = null;
    const next = latestOrder.current;
    const changed = !!next && next.some((post, index) => post.id !== session.origin[index]?.id);
    latestOrder.current = null;
    setDraggingId(null);
    setDragOrder(null);
    if (changed && next) {
      saveAll(next).catch(() => setMessage('Could not save the new order.'));
    }
  };

  const nudgePost = (postId: string, direction: -1 | 1) => {
    const index = posts.findIndex((post) => post.id === postId);
    const target = posts[index + direction];
    if (!target) return;
    const next = movePost(postId, target.id, posts);
    if (!next) return;
    saveAll(next).catch(() => setMessage('Could not save the new order.'));
  };

  const chooseHomeCount = (count: number) => {
    const homepageBlogCount = clampHomepageStoryCount(count);
    if (homepageBlogCount === homeCount) return;
    saveGlobalDisplaySettings({ ...displaySettings, homepageBlogCount }).then((saved) => {
      if (!saved) setMessage('Could not save how many stories appear on the homepage.');
    });
  };

  const removePost = (post: BlogPost) => {
    const title = post.title || 'Untitled';
    if (!window.confirm(`Delete "${title}"?`)) return;
    saveAll(posts.filter((item) => item.id !== post.id)).catch(() => setMessage('Could not delete the post.'));
  };

  const onTitle = (title: string) => {
    setEditing((current) => current && ({
      ...current,
      title,
      h1: h1Touched ? current.h1 : title,
      slug: slugTouched ? current.slug : slugFrom(title),
    }));
  };

  const imagePixelSize = (file: File) => new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the image'));
    };
    img.src = url;
  });

  const uploadImage = async (file: File, alt: string) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Could not read the image'));
      reader.readAsDataURL(file);
    });
    const response = await fetch(`${API_BASE_URL}/blog-images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataUrl, filename: file.name, alt }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Upload failed');
    return body;
  };

  const applyCommand = (command: string) => {
    const editor = document.getElementById('blog-editor');
    editor?.focus();
    document.execCommand(command);
    if (command === 'italic' && editor) {
      const italics = editor.querySelectorAll('i');
      for (let index = 0; index < italics.length; index += 1) {
        const node = italics[index];
        const em = document.createElement('em');
        em.innerHTML = node.innerHTML;
        node.replaceWith(em);
      }
    }
    if (editor) {
      editorHtml.current = editor.innerHTML;
      editor.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const editorBlock = (node: Node | null, editor: HTMLElement): HTMLElement | null => {
    let current: Node | null = node;
    while (current && current !== editor && current.parentNode !== editor) current = current.parentNode;
    if (!current || current === editor || current.parentNode !== editor) return null;
    if (current instanceof HTMLElement) return current;
    const wrapper = document.createElement('p');
    current.parentNode?.insertBefore(wrapper, current);
    wrapper.appendChild(current);
    return wrapper;
  };

  const formatArticleBlock = (tag: 'h2' | 'h3' | 'blockquote') => {
    const editor = document.getElementById('blog-editor');
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);
    const startBlock = editorBlock(range.startContainer, editor);
    const endBlock = editorBlock(range.endContainer, editor);
    if (!startBlock || startBlock.closest('.photo-row, .photo-masonry, .blog-rule, .blog-logo')) return;
    if (endBlock && startBlock !== endBlock) {
      const narrowed = document.createRange();
      narrowed.selectNodeContents(startBlock);
      selection.removeAllRanges();
      selection.addRange(narrowed);
    }
    let block = startBlock;
    if (block.querySelector('br')) {
      const caret = selection.anchorNode;
      const pieces: HTMLElement[] = [];
      let current = document.createElement('p');
      const nodes = block.childNodes;
      for (let index = 0; index < nodes.length; index += 1) {
        const node = nodes[index];
        if (node.nodeName === 'BR') {
          pieces.push(current);
          current = document.createElement('p');
        } else {
          current.appendChild(node);
          index -= 1;
        }
      }
      pieces.push(current);
      pieces.forEach((piece) => {
        if (!piece.innerHTML.trim()) piece.appendChild(document.createElement('br'));
        block.parentNode?.insertBefore(piece, block);
      });
      block.remove();
      block = pieces.find((piece) => caret && piece.contains(caret)) || pieces[0];
      const next = document.createRange();
      next.selectNodeContents(block);
      selection.removeAllRanges();
      selection.addRange(next);
    }
    const anchors = block.querySelectorAll('a');
    for (let index = 0; index < anchors.length; index += 1) {
      const anchor = anchors[index];
      const current = window.getSelection();
      if (current && current.rangeCount > 0 && current.containsNode(anchor, true)) continue;
      const paragraph = document.createElement('p');
      paragraph.appendChild(anchor);
      if (block.parentNode) block.parentNode.insertBefore(paragraph, block.nextSibling);
      index -= 1;
    }
    document.execCommand('formatBlock', false, tag);
    editorHtml.current = editor.innerHTML;
    editor.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const escapeAttr = (value: string) => value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

  const figureHtml = (image: { secure_url: string; width?: number; height?: number }, alt: string, caption: string, sizes?: string) => {
    const captionHtml = caption.trim() ? `<figcaption>${escapeAttr(caption.trim())}</figcaption>` : '';
    const sizeAttr = sizes ? ` sizes="${sizes}"` : '';
    return `<figure><img src="${image.secure_url}" alt="${escapeAttr(alt.trim())}" width="${image.width || 1200}" height="${image.height || 800}"${sizeAttr}>${captionHtml}</figure>`;
  };

  const rememberCaret = () => {
    const editor = document.getElementById('blog-editor');
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) savedRange.current = range.cloneRange();
  };

  const insertIntoEditor = (html: string) => {
    const editor = document.getElementById('blog-editor');
    if (!editor) return;
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const node = template.content.firstElementChild;
    if (!node) return;
    const range = savedRange.current;
    let block: Node | null = range && editor.contains(range.startContainer) ? range.startContainer : null;
    if (block && block.nodeType === Node.TEXT_NODE) block = block.parentNode;
    while (block && block.parentNode && block.parentNode !== editor) block = block.parentNode;
    if (block && block.parentNode === editor) block.parentNode.insertBefore(node, block.nextSibling);
    else editor.appendChild(node);
    const follow = document.createElement('p');
    follow.appendChild(document.createElement('br'));
    node.after(follow);
    const after = document.createRange();
    after.setStart(follow, 0);
    after.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(after);
    savedRange.current = after.cloneRange();
    editorHtml.current = editor.innerHTML;
    editor.dispatchEvent(new Event('input', { bubbles: true }));
    node.scrollIntoView({ block: 'nearest' });
    layoutPhotoMasonry(editor, { simulatePreview: true });
    setMasonryTick((tick) => tick + 1);
  };

  const insertDivider = () => {
    insertIntoEditor('<div class="blog-rule" role="separator" contenteditable="false"></div>');
  };

  const insertLogo = (size: 'sm' | 'md' | 'lg') => {
    const logo = studioLogo();
    if (!logo.url) {
      setMessage('Add a logo in Logo settings first.');
      return;
    }
    insertIntoEditor(`<div class="blog-logo blog-logo-${size}" contenteditable="false"><img src="${escapeAttr(logo.url)}" alt="${escapeAttr(logo.alt)}" width="${logo.width}" height="${logo.height}"></div>`);
  };

  const photoPrompt = (index: number, total: number, fileName: string, ask: string) => {
    const place = total === 1 ? 'the only photo' : `photo ${index} of ${total}, left to right`;
    return window.prompt(`Labelling ${place}.\nFile: ${fileName}\n${ask}`) || '';
  };

  const photoGroupHtml = (figures: string[], columns: MasonryColumns) => {
    if (figures.length === 1 && columns === 1) return figures[0];
    return `<div class="photo-masonry photo-masonry-${columns}">${figures.join('')}</div>`;
  };

  const placedMessage = (count: number, columns: MasonryColumns) => {
    if (count === 1 && columns === 1) return 'Added the photograph to the article.';
    if (columns === 1) return `Added ${count} photographs in one column.`;
    const noun = count === 1 ? 'photograph' : 'photographs';
    return `Added ${count} ${noun} in ${columns} columns.`;
  };

  const insertPhotos = async (files: File[]) => {
    if (!files.length) return;
    const chosen = files.slice(0, 12);
    const figures: string[] = [];
    for (let index = 0; index < chosen.length; index += 1) {
      const file = chosen[index];
      const alt = photoPrompt(index + 1, chosen.length, file.name, 'Describe what is in this photo, in a few words.');
      if (!alt.trim()) {
        setMessage(chosen.length === 1
          ? 'Every photo needs a short description.'
          : `Photo ${index + 1} of ${chosen.length} needs a short description. The photographs were not added.`);
        return;
      }
      const caption = photoPrompt(index + 1, chosen.length, file.name, 'Caption (optional). This is shown under the photo. Leave blank to skip.');
      const image = await uploadImage(file, alt);
      figures.push(figureHtml(image, alt, caption));
    }
    insertIntoEditor(photoGroupHtml(figures, masonryColumns));
    setMessage(placedMessage(figures.length, masonryColumns));
  };

  const applyColumnsInEditor = (columns: MasonryColumns) => {
    setMasonryColumns(columns);
    const editor = document.getElementById('blog-editor');
    if (!editor) return;
    editor.querySelectorAll('.photo-masonry').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.classList.remove('photo-masonry-1', 'photo-masonry-2', 'photo-masonry-3', 'photo-masonry-4', 'photo-masonry-5');
      node.classList.add(`photo-masonry-${columns}`);
      delete node.dataset.pack;
    });
    layoutPhotoMasonry(editor, { simulatePreview: true });
    editorHtml.current = editor.innerHTML;
    editor.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const restoreCaret = () => {
    const editor = document.getElementById('blog-editor');
    const range = savedRange.current;
    if (!editor) return;
    editor.focus();
    if (!range || !editor.contains(range.startContainer)) return;
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const tidyEditorLinks = (editor: HTMLElement) => {
    editor.querySelectorAll('a').forEach((anchor) => {
      const current = anchor.getAttribute('href') || '';
      const next = normalizeEditorHref(current, window.location.origin);
      if (next !== current) anchor.setAttribute('href', next);
    });
  };

  const insertLink = (href: string, label: string) => {
    const editor = document.getElementById('blog-editor');
    if (!editor) return;
    const relative = normalizeEditorHref(href, window.location.origin);
    restoreCaret();
    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const inside = Boolean(range && editor.contains(range.commonAncestorContainer));
    if (inside && selection && !selection.isCollapsed) {
      document.execCommand('createLink', false, relative);
    } else {
      document.execCommand('insertHTML', false, `<a href="${escapeAttr(relative)}">${escapeAttr(label)}</a>`);
    }
    tidyEditorLinks(editor);
    editorHtml.current = editor.innerHTML;
    editor.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const publishedStoryLinks = posts
    .filter((post) => post.status === 'published' && post.slug)
    .map((post) => ({ label: post.title || post.slug, href: `/blog/${post.slug}` }));

  const addTypedLink = () => {
    const href = normalizeEditorHref(customLink.trim(), window.location.origin);
    if (!href.startsWith('/') || href.startsWith('//')) {
      setMessage('Use an address on this site, starting with /.');
      return;
    }
    insertLink(href, href);
    setCustomLink('');
  };

  const openPost = (post: BlogPost, fresh: boolean) => {
    photoBlockRef.current = null;
    setPhotoDraft(null);
    setSlugTouched(!fresh && Boolean(post.slug));
    setH1Touched(Boolean(post.h1) && post.h1 !== post.title);
    setChooseSpecific((post.galleryIds || []).length > 0);
    setMetaTouched(Boolean((post.metaDescription || '').trim()));
    setAutoMeta(suggestMeta(post.content || ''));
    setGalleryQuery('');
    setMessage('');
    const savedColumns = String(post.content || '').match(/\bphoto-masonry-([1-5])\b/);
    setMasonryColumns(asMasonryColumns(Number(savedColumns?.[1])));
    editorHtml.current = normalizeEditorHtml(post.content || '<p></p>');
    savedRange.current = null;
    setEditing(post);
  };

  const persistEditing = async (options?: { keepOpen?: boolean }): Promise<BlogPost | null> => {
    if (!editing) return null;
    const editor = document.getElementById('blog-editor') as HTMLElement | null;
    if (editor) {
      flattenPhotoMasonry(editor);
      tidyEditorLinks(editor);
    }
    const content = normalizeEditorHtml(editor?.innerHTML || editing.content);
    if (editor) layoutPhotoMasonry(editor, { simulatePreview: true });
    const bareDomain = /^https?:\/\/carmenlorraine\.com\/?$/i;
    if (bareDomain.test((editing.canonical || '').trim())) {
      setMessage('Leave the canonical blank. It is filled in automatically.');
      return null;
    }
    const selectedCategories = chosenCategorySlugs(editing, postCategories);
    const category = selectedCategories[0] || '';
    const metaDescription = (metaTouched ? editing.metaDescription : ((editing.metaDescription || '').trim() || autoMeta)) || '';
    const alt = (editing.featuredImageAlt || '').trim();
    const heroImage = editing.heroImage
      ? { ...editing.heroImage, alt }
      : undefined;
    const slug = slugFrom(editing.slug || editing.title);
    const nextPost = normalizeBlogPost({
      ...editing,
      slug,
      excerpt: (editing.excerpt || '').trim(),
      content,
      category,
      categories: selectedCategories,
      galleryCategory: category || '',
      galleryIds: chooseSpecific ? (editing.galleryIds || []) : [],
      metaDescription: metaDescription.trim(),
      featuredImageAlt: alt,
      heroImage,
      canonical: (editing.canonical || '').trim() || `https://carmenlorraine.com/blog/${slug}`,
      published: editing.status === 'published',
      tags: [],
    });
    const publishing = nextPost.status === 'published' || nextPost.status === 'scheduled';
    const missing = [
      nextPost.title.trim() ? '' : 'title',
      category ? '' : 'category',
      (nextPost.heroImage?.secure_url || nextPost.heroImage?.url) ? '' : 'featured image',
      nextPost.featuredImageAlt?.trim() ? '' : 'alt text',
      wordCount(content) > 0 ? '' : 'article',
      nextPost.metaDescription?.trim() ? '' : 'meta description',
    ].filter(Boolean);
    if (publishing && missing.length) {
      setMessage(`Finish the checklist before publishing: ${missing.join(', ')}.`);
      return null;
    }
    if (!slug) {
      setMessage('Add a title before previewing.');
      return null;
    }
    const next = posts.some((post) => post.id === nextPost.id)
      ? posts.map((post) => (post.id === nextPost.id ? nextPost : post))
      : [...posts, { ...nextPost, id: nextPost.id || `post-${Date.now()}`, createdAt: new Date().toISOString() }];
    try {
      await saveAll(next);
      editorHtml.current = content;
      if (options?.keepOpen) {
        setEditing(nextPost);
        return nextPost;
      }
      photoBlockRef.current = null;
      setPhotoDraft(null);
      setEditing(null);
      return nextPost;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not save the post.');
      return null;
    }
  };

  const save = async () => {
    const saved = await persistEditing();
    if (saved) setMessage('Saved.');
  };

  const previewPost = async () => {
    if (!editing) return;
    const tab = window.open('about:blank', '_blank');
    setMessage('Saving…');
    const saved = await persistEditing({ keepOpen: true });
    if (!saved?.slug) {
      tab?.close();
      return;
    }
    const plain = `/blog/preview/${saved.slug}`;
    let href = previewHref && previewHref.includes(saved.slug) ? previewHref : plain;
    try {
      const response = await fetch(`${API_BASE_URL}/blog-preview-token?slug=${encodeURIComponent(saved.slug)}`, { credentials: 'include' });
      if (response.ok) {
        const body = await response.json();
        if (body?.path) href = body.path;
      }
    } catch {
      /* use plain preview path */
    }
    setPreviewHref(href);
    if (tab) tab.location.href = href;
    else window.open(href, '_blank', 'noopener,noreferrer');
    setMessage('Saved. Preview opened.');
  };

  const closePicker = () => {
    setPickerCount(null);
    setPickerMasonry(false);
    setPickerTarget('article');
  };

  const syncEditorHtml = (editor: HTMLElement) => {
    lockInserts(editor);
    const html = editor.innerHTML;
    editorHtml.current = html;
    setArticleWords(wordCount(html));
    if (!metaTouched) setAutoMeta(suggestMeta(html));
  };

  const openPhotoEditor = (element: HTMLElement) => {
    const block = readArticleBlock(element);
    if (!block) return;
    photoBlockRef.current = element;
    setPhotoDraft(block);
  };

  const closePhotoEditor = () => {
    photoBlockRef.current = null;
    setPhotoDraft(null);
  };

  const appendPhotos = (incoming: EditablePhoto[]) => {
    setPhotoDraft((current) => {
      if (!current || !incoming.length) return current;
      const photos = current.photos.concat(incoming).slice(0, 12);
      const kind = current.kind === 'figure' || (current.kind === 'row' && photos.length > 3) ? 'masonry' : current.kind;
      return { ...current, kind, photos };
    });
  };

  const uploadIntoDraft = async (files: File[]) => {
    const room = Math.max(0, 12 - (photoDraft?.photos.length || 0));
    const chosen = files.slice(0, room);
    if (!chosen.length) {
      setMessage('This group already has 12 photographs.');
      return;
    }
    const added: EditablePhoto[] = [];
    try {
      for (let index = 0; index < chosen.length; index += 1) {
        const file = chosen[index];
        const image = await uploadImage(file, 'Photograph');
        const src = image.secure_url || image.url || '';
        if (!src) continue;
        added.push({
          src,
          alt: '',
          caption: '',
          width: image.width,
          height: image.height,
        });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Upload failed');
    }
    appendPhotos(added);
    if (added.length) setMessage(added.some((photo) => !photo.alt) ? 'Add a short description for each new photograph.' : 'Photographs added.');
  };

  const addPhotosFromGallery = (photos: PickedPhoto[]) => {
    appendPhotos(photos.map((photo) => ({
      src: photo.src,
      alt: photo.alt,
      caption: photo.caption,
      width: photo.width,
      height: photo.height,
    })));
    closePicker();
    setMessage('Photographs added. Update the article to keep them.');
  };

  const commitPhotoDraft = (block: EditableBlock) => {
    const element = photoBlockRef.current;
    const editor = document.getElementById('blog-editor');
    if (!element || !editor || !editor.contains(element)) {
      closePhotoEditor();
      return;
    }
    const html = articleBlockHtml(block);
    if (!html) {
      const placeholder = document.createElement('p');
      placeholder.appendChild(document.createElement('br'));
      element.replaceWith(placeholder);
    } else {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const next = template.content.firstElementChild;
      if (next) element.replaceWith(next);
    }
    syncEditorHtml(editor);
    layoutPhotoMasonry(editor, { simulatePreview: true });
    setMasonryTick((tick) => tick + 1);
    closePhotoEditor();
    setMessage(block.photos.length ? 'Updated the photographs.' : 'Removed the photographs.');
  };

  const useFeaturedFromGallery = (photos: PickedPhoto[]) => {
    const photo = photos[0];
    if (!photo || !editing) return;
    const alt = (editing.featuredImageAlt || '').trim() || photo.alt;
    setEditing({
      ...editing,
      featuredImageAlt: alt,
      heroImage: {
        publicId: photo.publicId || photo.src,
        secure_url: photo.src,
        url: photo.src,
        width: photo.width,
        height: photo.height,
        alt,
      },
    });
    setMessage('Featured photograph chosen from your galleries.');
    closePicker();
  };

  const insertGalleryPhotos = (photos: PickedPhoto[]) => {
    const chosen = photos.slice(0, 12);
    if (!chosen.length) return;
    const figures = chosen.map((photo) => figureHtml(
      { secure_url: photo.src, width: photo.width, height: photo.height },
      photo.alt,
      photo.caption,
      masonryColumns === 1 && chosen.length === 1 ? '(max-width: 720px) 100vw, 640px' : undefined,
    ));
    insertIntoEditor(photoGroupHtml(figures, masonryColumns));
    setMessage(placedMessage(figures.length, masonryColumns));
    closePicker();
  };

  if (editing) {
    const seoValue = editing.seoTitle || '';
    const metaValue = metaTouched ? (editing.metaDescription || '') : ((editing.metaDescription || '').trim() || autoMeta);
    const seoLength = seoValue.length;
    const metaLength = metaValue.length;
    const previewTitle = seoValue || `${editing.title || 'Post title'} | Cape Town Photographer`;
    const previewDescription = metaValue || 'The two lines under the headline in Google.';
    const publishedGalleries = galleries.filter((gallery) => gallery.published !== false);
    const query = galleryQuery.trim().toLowerCase();
    const visibleGalleries = publishedGalleries.filter((gallery) => !query || gallery.name.toLowerCase().includes(query));
    const selectedGalleries = publishedGalleries.filter((gallery) => (editing.galleryIds || []).includes(String(gallery.id)));
    const hasFeatured = Boolean(editing.heroImage?.secure_url || editing.heroImage?.url);
    const selectedCategories = chosenCategorySlugs(editing, postCategories);
    const categorySlug = selectedCategories[0] || '';
    const primaryCategoryName = postCategories.find((category) => category.slug === categorySlug)?.name || '';
    const toggleCategory = (slug: string) => {
      const next = selectedCategories.includes(slug)
        ? selectedCategories.filter((item) => item !== slug)
        : selectedCategories.concat(slug);
      setEditing({ ...editing, category: next[0] || '', categories: next, galleryCategory: next[0] || '' });
    };
    const checks = [
      { label: 'Title', ok: Boolean(editing.title.trim()) },
      { label: 'Blog category', ok: Boolean(categorySlug) },
      { label: 'Featured image', ok: hasFeatured },
      { label: 'Alt text', ok: Boolean((editing.featuredImageAlt || '').trim()) },
      { label: 'Article', ok: articleWords > 0 },
      { label: 'Meta description', ok: Boolean(metaValue.trim()) },
    ];
    const toggleGallery = (id: string) => {
      const ids = new Set(editing.galleryIds || []);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      setEditing({ ...editing, galleryIds: Array.from(ids) });
    };
    const publishValue = editing.status === 'scheduled' ? editing.scheduledFor : editing.publishedAt;
    return (
      <div className="admin-section blog-management blog-editor-form" ref={editorFormRef}>
        <div className="section-controls" ref={sectionControlsRef}>
          <button type="button" className="btn btn-secondary" onClick={() => { photoBlockRef.current = null; setPhotoDraft(null); setEditing(null); }}>Back</button>
          <button type="button" className="btn" onClick={save}>Save</button>
          <button type="button" className="btn btn-secondary" onClick={() => { previewPost().catch(() => undefined); }} disabled={!editing.title.trim() && !editing.slug}>
            Preview
          </button>
        </div>
        {message ? <p className="blog-form-message">{message}</p> : null}
        <p className="blog-checklist">
          Ready to publish:{' '}
          {checks.map((item) => (
            <span key={item.label} className={item.ok ? 'is-done' : 'is-missing'}>{item.ok ? '✓' : '✗'} {item.label}</span>
          ))}
        </p>

        <section className="blog-form-section">
          <label className="blog-field">Title
            <input value={editing.title} onChange={(event) => onTitle(event.target.value)} />
            <Hint text="The headline readers see. Include the main topic and 'Cape Town', e.g. 'Best Family Photo Locations in Cape Town'." />
          </label>
          <label className="blog-field">Introduction
            <textarea rows={3} value={editing.excerpt || ''} onChange={(event) => setEditing({ ...editing, excerpt: event.target.value })} />
            <Hint text="The sentence under the title. One or two lines. Leave this blank to start straight with the photograph." />
          </label>
          <fieldset className="blog-field blog-category-choices">
            <legend>Blog categories</legend>
            {postCategories.map((category) => (
              <label key={category.slug}>
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.slug)}
                  onChange={() => toggleCategory(category.slug)}
                />
                {category.name}
              </label>
            ))}
            <Hint text="A story can sit in more than one section. The first one you tick is the main section, and its gallery supplies the photos at the end." />
          </fieldset>
        </section>

        <section className="blog-form-section">
          <h2>Photos</h2>
          <p className="blog-field-note">This photograph sits at the top of the article and is shown in full. Use a landscape photo — wider than it is tall. It is also the picture shown when the post is shared.</p>
          <div className="blog-featured-sources">
            <label className="blog-field">Featured image
              <input type="file" accept="image/*" onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                imagePixelSize(file).then((size) => {
                  if (size.width <= size.height) {
                    window.alert('Choose a landscape photo — wider than it is tall. It sits at the top of the article.');
                    return null;
                  }
                  const alt = (editing.featuredImageAlt || '').trim();
                  return uploadImage(file, alt);
                }).then((image) => {
                  if (!image) return;
                  setEditing({ ...editing, heroImage: { publicId: image.publicId, secure_url: image.secure_url, url: image.secure_url, width: image.width, height: image.height, alt: (editing.featuredImageAlt || '').trim() } });
                }).catch((error) => setMessage(error.message));
              }} />
            </label>
            <button type="button" onClick={() => { setPickerTarget('featured'); setPickerMasonry(false); setPickerCount(1); }}>
              Choose from galleries
            </button>
          </div>
          {editing.heroImage?.secure_url ? (
            <img className="blog-featured-preview" src={editing.heroImage.secure_url} alt={editing.featuredImageAlt || ''} />
          ) : <p className="blog-field-note">No featured image yet. Choose a landscape photo.</p>}
          <label className="blog-field">Alt text
            <input value={editing.featuredImageAlt || ''} onChange={(event) => setEditing({ ...editing, featuredImageAlt: event.target.value })} />
            <Hint text="Describe the photo in a few words for Google and screen readers, e.g. 'Family walking on Noordhoek Beach at sunset'." />
          </label>
          <p className="blog-field-note">Add more photographs inside the article. Upload a file, or choose photographs that are already in a gallery.</p>
        </section>

        <section className="blog-form-section">
          <ArticleHelp />
          <div className="blog-editor-dock">
            <div className="blog-editor-sticky">
              <div className="blog-toolbar" role="toolbar" aria-label="Article formatting">
                <div className="blog-tool-group">
                  <span className="blog-tool-label">Text</span>
                  <div className="blog-tool-buttons">
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('bold')}>Bold</button>
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('italic')}>Italic</button>
                  </div>
                </div>
                <div className="blog-tool-group">
                  <span className="blog-tool-label">Structure</span>
                  <div className="blog-tool-buttons">
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => formatArticleBlock('h2')}>Heading</button>
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => formatArticleBlock('h3')}>Subheading</button>
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('insertUnorderedList')}>Bullet list</button>
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('insertOrderedList')}>Numbered list</button>
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => formatArticleBlock('blockquote')}>Quote</button>
                    <button type="button" onMouseDown={(event) => { event.preventDefault(); rememberCaret(); }} onClick={insertDivider}>Divider</button>
                  </div>
                </div>
                <button
                  type="button"
                  className="blog-insert-toggle"
                  aria-expanded={insertOpen}
                  aria-controls="blog-insert-panel"
                  onMouseDown={(event) => { event.preventDefault(); rememberCaret(); }}
                  onClick={() => setInsertOpen((open) => !open)}
                >
                  Insert {insertOpen ? '▴' : '▾'}
                </button>
              </div>
              {insertOpen ? (
                <div id="blog-insert-panel" className="blog-insert-panel blog-insert-tools">
                  <section className="blog-insert-card">
                    <h3>Photos</h3>
                    <p className="blog-field-note">One photo at full width, or several in columns.</p>
                    <div className="blog-insert-actions">
                      <label className="blog-upload-button">
                        Upload photos
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onMouseDown={rememberCaret}
                          onChange={(event) => {
                            const files = Array.from(event.target.files || []);
                            event.target.value = '';
                            if (files.length) insertPhotos(files).catch((error) => setMessage(error.message));
                          }}
                        />
                      </label>
                      <button type="button" onMouseDown={(event) => { event.preventDefault(); rememberCaret(); }} onClick={() => { setPickerTarget('article'); setPickerMasonry(true); setPickerCount(1); }}>Choose from galleries</button>
                    </div>
                    <label className="blog-insert-field">Columns
                      <select value={masonryColumns} onMouseDown={rememberCaret} onChange={(event) => applyColumnsInEditor(asMasonryColumns(Number(event.target.value)))}>
                        <option value={1}>1 — single</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                      </select>
                    </label>
                  </section>
                  <section className="blog-insert-card">
                    <h3>Link</h3>
                    <p className="blog-field-note">Link the highlighted words.</p>
                    <label className="blog-insert-field">Page
                      <select value="" onMouseDown={rememberCaret} onChange={(event) => {
                        const chosen = event.target.value;
                        event.target.value = '';
                        const page = [...LINK_PAGES, ...publishedStoryLinks].find((item) => `${item.label}:::${item.href}` === chosen);
                        if (page) insertLink(page.href, page.label);
                      }}>
                        <option value="">Choose a page</option>
                        <optgroup label="Pages">
                          {LINK_PAGES.map((page) => <option key={`${page.label}:::${page.href}`} value={`${page.label}:::${page.href}`}>{page.label}</option>)}
                        </optgroup>
                        {publishedStoryLinks.length ? (
                          <optgroup label="Stories">
                            {publishedStoryLinks.map((page) => <option key={`${page.label}:::${page.href}`} value={`${page.label}:::${page.href}`}>{page.label}</option>)}
                          </optgroup>
                        ) : null}
                      </select>
                    </label>
                    <label className="blog-insert-field">or type an address
                      <span className="blog-link-row">
                        <input
                          type="text"
                          value={customLink}
                          placeholder="/blog/… or /galleries/…"
                          onMouseDown={rememberCaret}
                          onChange={(event) => setCustomLink(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            addTypedLink();
                          }}
                        />
                        <button type="button" onMouseDown={(event) => { event.preventDefault(); rememberCaret(); }} onClick={addTypedLink}>Add</button>
                      </span>
                    </label>
                  </section>
                  <section className="blog-insert-card">
                    <h3>Logo</h3>
                    <p className="blog-field-note">Studio logo at the cursor.</p>
                    <label className="blog-insert-field">Size
                      <select value="" onMouseDown={rememberCaret} onChange={(event) => {
                        const size = event.target.value;
                        event.target.value = '';
                        if (size === 'sm' || size === 'md' || size === 'lg') insertLogo(size);
                      }}>
                        <option value="">Choose a size</option>
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                        <option value="lg">Large</option>
                      </select>
                    </label>
                  </section>
                </div>
              ) : null}
            </div>
          <div
            id="blog-editor"
            key={editing.id}
            className="blog-body-editor"
            contentEditable
            suppressContentEditableWarning
            ref={(node) => {
              if (node && node.dataset.ready !== editing.id) {
                node.innerHTML = editorHtml.current || editing.content || '<p></p>';
                lockInserts(node);
                node.dataset.ready = editing.id;
              }
            }}
            onBlur={rememberCaret}
            onMouseDown={(event) => {
              if (event.button !== 0) return;
              const target = event.target;
              if (!(target instanceof Element) || target.closest('.blog-logo')) return;
              const block = target.closest('.photo-masonry, .photo-row') || target.closest('figure');
              if (!(block instanceof HTMLElement) || !event.currentTarget.contains(block)) return;
              event.preventDefault();
              openPhotoEditor(block);
            }}
            onInput={(event) => {
              lockInserts(event.currentTarget);
              const html = event.currentTarget.innerHTML;
              editorHtml.current = html;
              setArticleWords(wordCount(html));
              if (!metaTouched) setAutoMeta(suggestMeta(html));
            }}
          />
          </div>
          <p className="blog-field-note">Click a photograph in the article to change its description, caption, columns, or to remove it.</p>
          <p className={`blog-word-count${articleWords > 0 && (articleWords < 1000 || articleWords > 1400) ? ' is-off-target' : ''}`}>
            {articleWords} words · aim for 1,000–1,400
          </p>
        </section>

        <section className="blog-form-section">
          <h2>Questions & answers</h2>
          <Hint text="Add 3–5 questions clients often ask. These can appear directly in Google results." />
          {(editing.faq || []).map((item, index) => (
            <div className="blog-faq-item" key={`${editing.id}-faq-${index}`}>
              <label className="blog-field">Question
                <input value={item.question} onChange={(event) => {
                  const faq = [...(editing.faq || [])];
                  faq[index] = { ...faq[index], question: event.target.value };
                  setEditing({ ...editing, faq });
                }} />
              </label>
              <label className="blog-field">Answer
                <textarea rows={3} value={item.answer} onChange={(event) => {
                  const faq = [...(editing.faq || [])];
                  faq[index] = { ...faq[index], answer: event.target.value };
                  setEditing({ ...editing, faq });
                }} />
              </label>
              <div className="blog-faq-actions">
                <button type="button" disabled={index === 0} onClick={() => {
                  const faq = [...(editing.faq || [])];
                  const [moved] = faq.splice(index, 1);
                  faq.splice(index - 1, 0, moved);
                  setEditing({ ...editing, faq });
                }}>Move up</button>
                <button type="button" disabled={index === (editing.faq || []).length - 1} onClick={() => {
                  const faq = [...(editing.faq || [])];
                  const [moved] = faq.splice(index, 1);
                  faq.splice(index + 1, 0, moved);
                  setEditing({ ...editing, faq });
                }}>Move down</button>
                <button type="button" onClick={() => setEditing({ ...editing, faq: (editing.faq || []).filter((_, itemIndex) => itemIndex !== index) })}>Delete</button>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setEditing({ ...editing, faq: [...(editing.faq || []), { question: '', answer: '' }] })}>Add question</button>
        </section>

        <section className="blog-form-section">
          <h2>See more photos</h2>
          <Hint text="Shows photos from the matching gallery at the end of the post. Leave it on." />
          <label className="blog-check">
            <input type="checkbox" checked={editing.showGalleryBlock !== false} onChange={(event) => setEditing({ ...editing, showGalleryBlock: event.target.checked })} />
            Show this block
          </label>
          <label className="blog-field">Thumbnails
            <input type="number" min={1} max={12} disabled={editing.showGalleryBlock === false} value={editing.galleryCount || 6} onChange={(event) => setEditing({ ...editing, galleryCount: Math.min(12, Math.max(1, Number(event.target.value) || 6)) })} />
          </label>
          <button type="button" className="blog-text-button" onClick={() => {
            const next = !chooseSpecific;
            setChooseSpecific(next);
            if (!next) setEditing({ ...editing, galleryIds: [] });
          }}>
            {chooseSpecific ? 'Use the matching gallery instead' : 'Choose specific galleries instead'}
          </button>
          {chooseSpecific ? (
            <div className="blog-gallery-picker">
              <label className="blog-field">Search galleries
                <input value={galleryQuery} placeholder="Search galleries" onChange={(event) => setGalleryQuery(event.target.value)} />
              </label>
              {selectedGalleries.length ? (
                <ul className="blog-gallery-chips">
                  {selectedGalleries.map((gallery) => (
                    <li key={gallery.id}>
                      <button type="button" onClick={() => toggleGallery(String(gallery.id))}>{gallery.name} ×</button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <ul className="blog-gallery-scroll">
                {visibleGalleries.map((gallery) => {
                  const coverImage = gallery.heroImage || gallery.images?.[0];
                  const cover = coverImage?.secure_url || coverImage?.url || '';
                  const checked = (editing.galleryIds || []).includes(String(gallery.id));
                  return (
                    <li key={gallery.id}>
                      <label className={checked ? 'is-selected' : ''}>
                        <input type="checkbox" checked={checked} onChange={() => toggleGallery(String(gallery.id))} />
                        {/^https:\/\//i.test(cover) ? <img src={cover} alt="" /> : <span className="blog-gallery-cover-empty" aria-hidden="true" />}
                        <span>
                          <strong>{gallery.name}</strong>
                          <em>{gallery.imageCount || gallery.images?.length || 0} photographs</em>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="blog-field-note">{primaryCategoryName ? `Photos come from the ${primaryCategoryName} gallery.` : 'Choose a category above and photos come from that gallery.'}</p>
          )}
        </section>

        <details className="blog-form-section blog-disclosure">
          <summary>Google settings</summary>
          <label className="blog-field">Slug
            <input value={editing.slug} placeholder="filled in from the title" onChange={(event) => { setSlugTouched(true); setEditing({ ...editing, slug: slugFrom(event.target.value) }); }} />
            <Hint text="The web address of the post. Filled in automatically — only change it before publishing." />
          </label>
          <label className="blog-field">H1
            <input value={editing.h1 || ''} placeholder={editing.title || 'Same as the title'} onChange={(event) => { setH1Touched(true); setEditing({ ...editing, h1: event.target.value }); }} />
            <Hint text="The main heading on the page. Leave it as the title unless you have a reason." />
          </label>
          <label className="blog-field">SEO title
            <input value={seoValue} placeholder={`${editing.title || 'Post title'} | Cape Town Photographer`} onChange={(event) => setEditing({ ...editing, seoTitle: event.target.value })} />
            <Hint text="The blue headline in Google. Leave blank to use the title. Max 60 characters." />
          </label>
          <p className={`blog-counter${seoLength > 60 ? ' is-warn' : ''}`}>{seoValue ? `${seoLength}${seoLength > 60 ? ' · over 60' : ''}` : 'Left blank — Google will use the title.'}</p>
          <label className="blog-field">Meta description
            <textarea rows={3} value={metaValue} onChange={(event) => { setMetaTouched(true); setEditing({ ...editing, metaDescription: event.target.value }); }} />
            <Hint text="The two lines under the headline in Google. Write a short invitation to read, max 155 characters." />
          </label>
          <p className={`blog-counter${metaLength > 155 ? ' is-warn' : ''}`}>{metaLength}{metaLength > 155 ? ' · over 155' : ''}</p>
          <label className="blog-field">Canonical
            <input value={editing.canonical || ''} placeholder={`https://carmenlorraine.com/blog/${editing.slug || 'slug'}`} onChange={(event) => setEditing({ ...editing, canonical: event.target.value })} />
            <Hint text="Leave blank. This is set automatically." />
          </label>
          <label className="blog-check">
            <input type="checkbox" checked={Boolean(editing.noindex)} onChange={(event) => setEditing({ ...editing, noindex: event.target.checked })} />
            Hide from search
          </label>
          <Hint text="Only tick this for private or test posts. Ticked posts won't appear in Google." />
          <div className="blog-google-preview">
            <p>{previewTitle}</p>
            <p>https://carmenlorraine.com/blog/{editing.slug || 'slug'}</p>
            <p>{previewDescription}</p>
          </div>
        </details>

        <details className="blog-form-section blog-disclosure blog-cta-card">
          <summary>Contact button</summary>
          <Hint text="Every post ends with a Contact me button. It opens WhatsApp with a note about this story. The number is the studio number from website content." />
        </details>

        <section className="blog-form-section">
          <h2>Publish</h2>
          <label className="blog-field">Status
            <select value={editing.status || 'draft'} onChange={(event) => setEditing({ ...editing, status: event.target.value as BlogPost['status'], publishedAt: editing.publishedAt || new Date().toISOString() })}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </select>
          </label>
          <label className="blog-field">Publish date
            <input
              type="datetime-local"
              value={/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(publishValue || '') ? (publishValue || '') : toLocalInput(publishValue)}
              onChange={(event) => {
                const next = editing.status === 'scheduled' ? event.target.value : fromLocalInput(event.target.value);
                if (editing.status === 'scheduled') setEditing({ ...editing, scheduledFor: next });
                else setEditing({ ...editing, publishedAt: next });
              }}
            />
          </label>
          <label className="blog-field">Author
            <input value={editing.author} onChange={(event) => setEditing({ ...editing, author: event.target.value })} />
          </label>
        </section>
        {photoDraft ? (
          <BlogPhotoEditor
            draft={photoDraft}
            onChange={setPhotoDraft}
            onClose={closePhotoEditor}
            onSave={commitPhotoDraft}
            onUpload={(files) => { uploadIntoDraft(files).catch((error) => setMessage(error.message)); }}
            onChooseGallery={() => { setPickerTarget('add'); setPickerMasonry(true); setPickerCount(1); }}
          />
        ) : null}
        {pickerCount ? (
          <BlogPhotoPicker
            count={1}
            masonry={pickerMasonry}
            columns={masonryColumns}
            onColumns={setMasonryColumns}
            arrangement="editorial"
            onArrangement={() => undefined}
            selectionLimit={pickerTarget === 'add' ? Math.max(1, 12 - (photoDraft?.photos.length || 0)) : undefined}
            requireLandscape={pickerTarget === 'featured'}
            onClose={closePicker}
            onInsert={pickerTarget === 'featured' ? useFeaturedFromGallery : pickerTarget === 'add' ? addPhotosFromGallery : insertGalleryPhotos}
          />
        ) : null}
      </div>
    );
  }

  const homeOptions = Array.from({ length: HOMEPAGE_STORY_LIMIT }, (_, index) => index + 1);
  const journalHidden = displaySettings.showBlogSection === false;

  return (
    <div className="admin-section blog-management">
      <div className="blog-index-bar">
        <div className="blog-index-intro">
          <p>
            {journalHidden
              ? 'The homepage journal is turned off in Display settings. This order is used when you turn it back on.'
              : 'Drag a story to change its place. The homepage uses the first published stories in this list. The first one is the large story.'}
          </p>
          <div className="blog-home-count" role="group" aria-label="Stories on the homepage">
            <span>On the homepage</span>
            {homeOptions.map((count) => (
              <button
                key={count}
                type="button"
                aria-pressed={homeCount === count}
                onClick={() => chooseHomeCount(count)}
              >
                {count}
              </button>
            ))}
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => openPost({ ...EMPTY, id: `post-${Date.now()}`, createdAt: new Date().toISOString(), publishedAt: new Date().toISOString() }, true)}>New post</button>
      </div>
      {message ? <p className="blog-form-message">{message}</p> : null}
      {listed.length === 0 ? (
        <p className="blog-index-empty">No stories yet. Use New post to write the first one.</p>
      ) : (
        <ol className="blog-story-list">
          {listed.map((post, index) => {
            const title = post.title || 'Untitled';
            const photo = imageSource(post.heroImage);
            const categoryName = chosenCategorySlugs(post, postCategories)
              .map((slug) => postCategories.find((category) => category.slug === slug)?.name || '')
              .filter(Boolean)
              .join(', ');
            const excerpt = (post.excerpt || '').trim();
            const role = homeRole.get(post.id);
            const date = formatStoryDate(post.publishedAt || post.createdAt);
            return (
              <li
                key={post.id}
                className={`blog-story-row${draggingId === post.id ? ' is-dragging' : ''}${role === 'lead' ? ' is-lead' : ''}${role === 'supporting' ? ' is-home' : ''}`}
                data-post-id={post.id}
              >
                <button
                  type="button"
                  className="blog-story-grip"
                  aria-label={`Reorder ${title}. Position ${index + 1}. Drag, or use the arrow keys.`}
                  title="Drag to reorder"
                  onPointerDown={(event) => startReorder(event, post.id)}
                  onPointerMove={moveReorder}
                  onPointerUp={finishReorder}
                  onPointerCancel={finishReorder}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                      event.preventDefault();
                      nudgePost(post.id, -1);
                    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                      event.preventDefault();
                      nudgePost(post.id, 1);
                    }
                  }}
                >
                  <i className="fas fa-grip-vertical" aria-hidden="true"></i>
                  <span>{index + 1}</span>
                </button>
                <button type="button" className="blog-story-open" onClick={() => openPost(post, false)}>
                  {photo ? (
                    <img src={cloudinaryDeliveryUrl(photo, 480)} alt="" />
                  ) : (
                    <span className="blog-story-photo-fallback" aria-hidden="true">No photo</span>
                  )}
                  <span className="blog-story-copy">
                    <span className="blog-story-title">{title}</span>
                    <span className="blog-story-meta">
                      <span className={`blog-story-status is-${post.status || (post.published ? 'published' : 'draft')}`}>{storyStatus(post)}</span>
                      {categoryName ? <span>{categoryName}</span> : null}
                      {date ? <span>{date}</span> : null}
                    </span>
                    {excerpt ? <span className="blog-story-excerpt">{excerpt}</span> : null}
                    {role ? (
                      <span className="blog-story-place">{role === 'lead' ? 'Large story on the homepage' : 'On the homepage'}</span>
                    ) : null}
                  </span>
                </button>
                <button type="button" className="blog-story-delete" aria-label={`Delete ${title}`} onClick={() => removePost(post)}>
                  <i className="fas fa-trash" aria-hidden="true"></i>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default BlogManagement;
